<template>
  <div class="row">
    <div class="col-xs-12">
      <div class="ibox">
        <div class="ibox-title">
          <div class="row">
            <div class="col-xs-12">
              <h1>Self Pause Subscription Settings</h1>
            </div>
          </div>
        </div>
        <div class="ibox-content">
          <div class="grid grid-cols-2 sm-grid-cols-1 gap-x-xl gap-y-lg">
            <div>
              <p class="font-semi m-b-m">
                Do you want subscribers to pause their own recurring
                subscriptions through WrestlingIQ without contacting you? Turning on this setting
                makes it so:
              </p>
              <ul class="self-pause-benefits">
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    Pauses are consistent and don't get forgotten by your staff,
                    avoiding indefinite subscription pauses.
                  </span>
                </li>
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    Parents select the number of billing periods they wish to
                    pause. They will not be invoiced during the paused billing
                    periods.
                  </span>
                </li>
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    Invoices and charges resume automatically when the pause
                    ends.
                  </span>
                </li>
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    If you offer expiring subscriptions with a set number of
                    recurring invoices, the payment schedule and end date shift
                    later by the length of the pause. Paused invoices are
                    delayed, not skipped.
                  </span>
                </li>
              </ul>
              <p class="self-pause-note text-gray-600">
                <small>
                  <b>Note:</b> yearly subscriptions can't be self-paused, and subscribers who
                  need something different are told to contact their coach.
                  Coaches and admins can always pause any subscription, for any
                  length, from the wrestler payments tab under View
                  Subscription.
                </small>
              </p>
              <form role="form" id="self-pause-form" @submit.prevent="update">
                <div class="form-group m-t-lg">
                  <label class="font-semi"
                    >Can subscribers pause their own subscription?</label
                  >
                  <div class="m-t-sm">
                    <label class="flex">
                      <input
                        type="radio"
                        name="self_pause_allowed"
                        :value="true"
                        v-model="selfPauseAllowed"
                      />
                      <div class="m-l-m flex flex-col">
                        <span class="font-semi">Yes</span>
                        <p class="font-normal m-b-none">
                          Subscribers can self-pause their subscriptions. Limits
                          set below.
                        </p>
                      </div>
                    </label>
                    <label class="flex m-t-m">
                      <input
                        type="radio"
                        name="self_pause_allowed"
                        :value="false"
                        v-model="selfPauseAllowed"
                      />
                      <div class="m-l-m flex flex-col">
                        <span class="font-semi">No</span>
                        <p class="font-normal m-b-none">
                          Subscribers who need a break must contact a coach to
                          pause
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
                <div class="form-group m-t-lg">
                  <label for="max_self_pause_intervals" class="font-semi"
                    >Longest pause a subscriber can choose</label
                  >
                  <p class="m-t-none m-b-sm">
                    Subscribers pick any number of billing periods from 1 up to
                    this limit. A billing period is one month on a monthly plan,
                    one quarter on a quarterly plan, and one week on a weekly
                    plan.
                  </p>
                  <select
                    id="max_self_pause_intervals"
                    name="max_self_pause_intervals"
                    v-model.number="maxIntervals"
                    class="form-control"
                    style="max-width: 240px"
                  >
                    <option v-for="n in intervalChoices" :key="n" :value="n">
                      {{ n }}
                      {{ n === 1 ? "billing period" : "billing periods" }}
                    </option>
                  </select>
                  <p class="text-gray-600 m-t-xs m-b-none">
                    <small>
                      For example, with a limit of 3 a subscriber on a monthly
                      plan can pause for 1, 2, or 3 months, and a subscriber on
                      a quarterly plan can pause for 3, 6, or 9 months.
                    </small>
                  </p>
                </div>
                <div class="form-group m-t-lg">
                  <label class="font-semi"
                    >How often can subscribers pause?</label
                  >
                  <div class="m-t-sm">
                    <label class="flex">
                      <input
                        type="radio"
                        name="self_pause_once_per_year"
                        :value="false"
                        v-model="oncePerYear"
                      />
                      <div class="m-l-m flex flex-col">
                        <span class="font-semi">Unlimited pauses</span>
                        <p class="font-normal m-b-none">
                          Subscribers can pause as often as they like
                        </p>
                      </div>
                    </label>
                    <label class="flex m-t-m">
                      <input
                        type="radio"
                        name="self_pause_once_per_year"
                        :value="true"
                        v-model="oncePerYear"
                      />
                      <div class="m-l-m flex flex-col">
                        <span class="font-semi">One pause every 12 months</span>
                        <p class="font-normal m-b-none">
                          A rolling window from the start of their last self
                          pause. Subscribers who've used theirs are told when
                          they can pause again.
                        </p>
                      </div>
                    </label>
                  </div>
                  <p class="text-gray-600 m-t-xs m-b-none">
                    <small>
                      Self pauses only. Coaches and admins can always pause any
                      subscription, and pauses you make don't count toward a
                      subscriber's limit.
                    </small>
                  </p>
                </div>
                <div class="form-group m-t-lg">
                  <label for="self-pause-policy-editor" class="font-semi"
                    >Your pause policy (optional)</label
                  >
                  <p class="m-t-none m-b-sm">
                    Shown to subscribers at the top of the pause screen. Use it
                    for anything the picker can't say for you: injury
                    exceptions, whether a pause holds their roster spot, or who
                    to contact for something longer.
                  </p>
                  <p class="text-gray-600 m-t-none m-b-sm">
                    <small>
                      Subscribers read this on a phone. Lead with the few rules
                      that change what they pick. Anything past the first few
                      lines is tucked behind a "Read the full policy" link, as
                      in the preview.
                    </small>
                  </p>
                  <input
                    id="self-pause-policy-editor"
                    type="hidden"
                    name="self_pause_policy_text"
                  />
                  <trix-editor
                    id="trix-self-pause-policy"
                    input="self-pause-policy-editor"
                    class="trix-content"
                  ></trix-editor>
                </div>
                <div class="m-t-lg flex align-items-center gap-x-xs">
                  <ladda-button
                    @lbClicked="update"
                    el-class="btn btn-primary"
                    :loading="updating"
                  >
                    Save Changes
                  </ladda-button>
                  <a :href="settingsPath" class="btn btn-default">Cancel</a>
                  <small v-if="error" class="text-danger m-l-sm">{{
                    errorMessage
                  }}</small>
                </div>
              </form>
            </div>
            <div class="pause-preview-well">
              <p class="upper-label m-t-none m-b-xxs">What subscribers see</p>
              <template v-if="selfPauseAllowed">
                <p class="m-b-m">
                  A live example of the pause screen with your limit of
                  <strong
                    >{{ maxIntervals }}
                    {{
                      maxIntervals === 1 ? "billing period" : "billing periods"
                    }}</strong
                  >.
                </p>
                <div v-if="samplePlans.length > 1" class="m-b-m">
                  <p class="font-semi m-b-xxs">Example plan</p>
                  <div
                    class="btn-group pause-preview-plans"
                    role="group"
                    aria-label="Example plan"
                  >
                    <button
                      v-for="plan in samplePlans"
                      :key="plan.key"
                      type="button"
                      class="btn btn-default btn-sm"
                      :class="{ active: samplePlanKey === plan.key }"
                      @click="samplePlanKey = plan.key"
                    >
                      {{ plan.label }}
                    </button>
                  </div>
                </div>
                <div class="pause-preview">
                  <div class="pause-modal__shell">
                    <header class="pause-modal__header">
                      <div class="min-w-0">
                        <h2 class="pause-modal__title">Pause subscription</h2>
                        <p class="pause-modal__subtitle">
                          Alex's subscription, billed on
                          {{ samplePlan.anchorDescription }}
                        </p>
                      </div>
                      <span class="pause-modal__close"
                        ><i class="fa-solid fa-xmark"></i
                      ></span>
                    </header>
                    <div class="pause-modal__body">
                      <div v-if="policyHtml" class="pause-modal__policy">
                        <p class="pause-modal__policy-heading">Pause policy</p>
                        <div
                          ref="previewPolicy"
                          class="pause-modal__policy-body trix-content"
                          :class="{
                            'is-collapsed':
                              previewPolicyOverflows && !previewPolicyExpanded,
                          }"
                          v-html="policyHtml"
                        ></div>
                        <button
                          v-if="previewPolicyOverflows"
                          type="button"
                          class="pause-modal__policy-toggle pause-preview__live"
                          @click="
                            previewPolicyExpanded = !previewPolicyExpanded
                          "
                        >
                          {{
                            previewPolicyExpanded
                              ? "Show less"
                              : "Read the full policy"
                          }}
                        </button>
                      </div>
                      <div class="pause-modal__section">
                        <p class="pause-modal__label">
                          When should the pause start?
                        </p>
                        <div class="pause-modal__segments">
                          <label class="pause-modal__segment is-selected">
                            <input type="radio" checked disabled />
                            <span>Right away</span>
                          </label>
                          <label class="pause-modal__segment">
                            <input type="radio" disabled />
                            <span>On a future date</span>
                          </label>
                        </div>
                      </div>
                      <div class="pause-modal__section">
                        <p class="pause-modal__label">How long?</p>
                        <div class="pause-modal__options">
                          <label
                            v-for="(option, index) in previewOptions"
                            :key="option.intervals"
                            class="pause-modal__option"
                            :class="{ 'is-selected': index === 0 }"
                          >
                            <input
                              type="radio"
                              :checked="index === 0"
                              disabled
                            />
                            <span class="pause-modal__option-body">
                              <span class="pause-modal__option-title">
                                {{ option.length }}
                                <span class="pause-modal__option-meta">{{
                                  option.periods
                                }}</span>
                              </span>
                              <span class="pause-modal__option-detail">{{
                                option.detail
                              }}</span>
                            </span>
                          </label>
                        </div>
                      </div>
                      <p class="pause-modal__note">
                        Payments resume automatically when the pause ends. Time
                        you've already paid for isn't refunded.
                        <template v-if="oncePerYear"
                          >Subscriptions can be paused online once every 12
                          months.</template
                        >
                      </p>
                    </div>
                    <footer class="pause-modal__footer">
                      <span class="btn btn-default">Cancel</span>
                      <span class="btn btn-warning">Pause subscription</span>
                    </footer>
                  </div>
                </div>
                <p class="text-gray-600 m-t-sm m-b-none">
                  <small
                    >Real options use each subscriber's own bill date.</small
                  >
                </p>
              </template>
              <p v-else class="text-gray-600 m-b-none">
                Subscribers won't see a pause option on their subscription page
                until you turn self pause on.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';
import { plainTextFromTrix } from '../trix_text';
import { POLICY_COLLAPSED_HEIGHT, policyOverflows } from '../pause_policy';

// Admin settings for member self-pause, with a live mock of the pause modal
// (pause_payments_modal.vue) so the coach can see what their limit turns into. The mock reuses
// the modal's CSS classes and mirrors BillingSubscription#self_pause_options against a sample
// bill grid; the real modal gets its options from the server for the member's actual grid.
// Fallback cadences for a team with no active pausable plans yet.
const DEFAULT_CADENCES = [
  { interval: 'month', interval_count: 1 },
  { interval: 'month', interval_count: 3 },
  { interval: 'week', interval_count: 1 },
];

// "Monthly plan", "Quarterly plan", "Weekly plan", "Every 2 months plan"
function cadenceLabel(unit, count) {
  if (count === 1) return unit === 'week' ? 'Weekly plan' : 'Monthly plan';
  if (unit === 'month' && count === 3) return 'Quarterly plan';
  return `Every ${count} ${unit}s plan`;
}

// Where the sample bill dates fall, for the mock's subtitle
function anchorDescription(unit, count) {
  const base = unit === 'week' ? 'Mondays' : 'the 1st of the month';
  return count === 1 ? base : `${base}, every ${count} ${unit}s`;
}

function samplePlanFor({ interval, interval_count: count }) {
  const unit = interval === 'week' ? 'week' : 'month';
  return {
    key: `${unit}-${count}`,
    label: cadenceLabel(unit, count),
    unit,
    count,
    anchorDescription: anchorDescription(unit, count),
  };
}

export default {
  name: 'self-pause-settings',
  components: { LaddaButton },
  mixins: [errorableMix],
  props: {
    teamId: {
      type: Number,
      required: true,
    },
    initialSelfPauseAllowed: {
      type: Boolean,
      default: false,
    },
    // Longest self-pause in whole billing periods (Team#max_self_pause_intervals, 1..12).
    initialMaxIntervals: {
      type: Number,
      default: 3,
    },
    // Team#self_pause_once_per_year: limit members to one self pause per rolling 12 months.
    initialOncePerYear: {
      type: Boolean,
      default: true,
    },
    // Club-authored pause rules as Trix HTML (Team#self_pause_policy_text). Shown to members at the
    // top of the pause modal; an empty policy shows nothing.
    initialPolicyText: {
      type: String,
      default: '',
    },
    settingsPath: {
      type: String,
      default: '/settings/team?tab=registration',
    },
    // [{ interval: 'week' | 'month', interval_count }] the team actually sells (active membership
    // plans on a pausable cadence). The switcher offers only these; empty falls back to defaults.
    examplePlanCadences: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    const cadences = this.examplePlanCadences.length
      ? this.examplePlanCadences
      : DEFAULT_CADENCES;
    const samplePlans = cadences.map(samplePlanFor);
    return {
      selfPauseAllowed: this.initialSelfPauseAllowed,
      maxIntervals: this.initialMaxIntervals,
      oncePerYear: this.initialOncePerYear,
      // Mirrors Team::MAX_SELF_PAUSE_INTERVALS_LIMIT
      intervalChoices: _.range(1, 13),
      samplePlans,
      samplePlanKey: samplePlans[0].key,
      // Live copy of the policy editor's HTML, so the mock modal reflects edits as they're typed
      policyHtml: this.initialPolicyText,
      policyTrixEditor: null,
      // Mirrors the real modal: a long policy shows collapsed in the mock too
      previewPolicyOverflows: false,
      previewPolicyExpanded: false,
      updating: false,
    };
  },
  watch: {
    policyHtml() {
      this.measurePreviewPolicy();
    },
    selfPauseAllowed(allowed) {
      if (allowed) this.measurePreviewPolicy();
    },
    samplePlanKey() {
      this.measurePreviewPolicy();
    },
  },
  mounted() {
    this.measurePreviewPolicy();
    document.addEventListener('trix-initialize', this.trixInit);
    document.addEventListener('trix-change', this.trixChange);
    document.addEventListener('trix-file-accept', this.trixFileAccept);
  },
  beforeDestroy() {
    document.removeEventListener('trix-initialize', this.trixInit);
    document.removeEventListener('trix-change', this.trixChange);
    document.removeEventListener('trix-file-accept', this.trixFileAccept);
  },
  computed: {
    samplePlan() {
      return _.find(this.samplePlans, { key: this.samplePlanKey });
    },
    // Same shape the server resolves for the real modal: resume date stepped from now by whole
    // periods.
    previewOptions() {
      const from = moment();
      const { unit, count } = this.samplePlan;
      return _.range(1, this.maxIntervals + 1).map((intervals) => {
        const resumesAt = from.clone().add(intervals * count, unit);
        const total = intervals * count;
        return {
          intervals,
          length: `${total} ${unit}${total === 1 ? '' : 's'}`,
          periods:
            intervals === 1
              ? '1 billing period'
              : `${intervals} billing periods`,
          detail: `Resumes ${resumesAt.format('M/D/YYYY')}.`,
        };
      });
    },
  },
  methods: {
    // Measured with the clamp released, so scrollHeight is the policy's natural height
    measurePreviewPolicy() {
      this.previewPolicyOverflows = false;
      this.previewPolicyExpanded = false;
      this.$nextTick(() => {
        this.previewPolicyOverflows = policyOverflows(
          this.$refs.previewPolicy,
          POLICY_COLLAPSED_HEIGHT,
        );
      });
    },
    trixInit(event) {
      if (event.target.id !== 'trix-self-pause-policy') return;
      this.policyTrixEditor = event.target.editor;
      if (this.initialPolicyText) {
        this.policyTrixEditor.loadHTML(this.initialPolicyText);
      }
    },
    trixChange(event) {
      if (event.target.id !== 'trix-self-pause-policy') return;
      this.policyHtml = plainTextFromTrix(event.target.value)
        ? event.target.value
        : '';
    },
    // No attachments in a policy blurb: nothing here uploads them, so an accepted file would embed
    // a dead blob URL.
    trixFileAccept(event) {
      if (event.target.id === 'trix-self-pause-policy') event.preventDefault();
    },
    update: _.throttle(function () {
      if (this.updating) return;

      const vm = this;
      vm.updating = true;
      vm.error = false;
      const url = vm.$apiService.teamUrl(vm.teamId);
      const data = {
        team: {
          self_pause_allowed: vm.selfPauseAllowed,
          max_self_pause_intervals: vm.maxIntervals,
          self_pause_once_per_year: vm.oncePerYear,
          // Trix leaves "<div><br></div>" behind for an empty editor; store nothing in that case
          self_pause_policy_text: vm.policyHtml,
        },
      };

      axios
        .put(url, data)
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            'Self pause settings saved',
            true,
          );
          vm.updating = false;
        })
        .catch((error) => {
          vm.updating = false;
          vm.errorMessage = `Error: ${error.toString()}`;
          vm.error = true;
        });
    }, 500),
  },
};
</script>
